import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { ServerCachedUser } from '@adornis/bh-util/util.js';
import { HTML } from '@adornis/cms/db/scalars.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { mongo } from '../server/mongo.js';
import { ColorDefinitions } from './ColorDefinitions.js';
import { GlobalSetting } from './global-setting.js';
import { Setting } from './settings.js';

@Entity()
export class Institution extends MongoEntity {
  static override _class = 'Institution';
  static override _collectionName = 'institutions';

  @validate(nonOptional())
  @Field(type => String)
  name!: string;

  // hidden: hidden from the participant-facing institution overview and product filter
  @Field(type => Boolean, { default: v => v ?? false })
  hidden!: boolean;

  // hiddenForAdmins: hidden from the admin institution picker; settings editor and deep links are unaffected
  @Field(type => Boolean, { default: v => v ?? false })
  hiddenForAdmins!: boolean;

  @Field(type => ID)
  logoID?: string;

  @Field(type => ID)
  emailBannerImageID: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  maintenanceMode!: boolean;

  @Field(type => HTML)
  maintenanceModeRender: Maybe<string>;

  @Field(type => String)
  shortDescription: Maybe<string>;

  @Field(type => String)
  ccMail: Maybe<string>;

  @Field(type => Setting, {
    resolve: function (this: Institution) {
      return async () => {
        const result = (await loadMongoData(Setting, { institutionID: this._id })) as Setting[];
        return result[0] ?? new Setting({ institutionID: this._id });
      };
    },
  })
  settings!: Setting;

  @Field(type => Int, { default: v => v ?? 14 })
  maximumParallelSignups: Maybe<number>;

  @Field(type => Boolean, { default: v => v ?? true })
  sortProductsByTime: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => ColorDefinitions, { default: v => v ?? new ColorDefinitions({}) })
  colorDefinitions!: ColorDefinitions;

  // OPTIONALLY OVERRIDE GLOBAL SETTINGS
  // Legal docs have been moved to separate InstitutionLegalDoc entities
  // Query them directly using InstitutionLegalDoc.getOrCreate() when needed

  @Field(type => GlobalSetting, {
    resolve: function (this: Institution) {
      return async (gqlFields: BaseQLSelectionSet<GlobalSetting>) => {
        // This operation also creates the singleton when it does not exist.
        const { getLegacyGlobalSetting } = await import('../api/global-settings.js');
        return getLegacyGlobalSetting()(gqlFields);
      };
    },
  })
  globalSettings: Maybe<GlobalSetting>;

  @Field(type => [String])
  plugins: Maybe<string[]>;

  @Field(type => Boolean, { default: value => value ?? false })
  _feature_counselings!: boolean;

  @Field(type => Boolean, {
    resolve: function (this: Institution) {
      return async () => {
        // Import inside the resolver to keep the server-only WhatsApp client out of client bundles.
        const { isWhatsappConfiguredForInstitution } = await import('../whatsapp/util.js');
        return isWhatsappConfiguredForInstitution(this._id);
      };
    },
  })
  whatsappEnabled!: boolean;

  // (aufsteigend sortiert)
  @Field(type => Int)
  sortingWeight?: number;

  static get allFields() {
    return selectionSet(() => this, 3);
  }

  static async getAllowedInstitutions(user: ServerCachedUser, adminAreaOnly: boolean = false) {
    const institutionCollection = mongo().collection(Institution._collectionName);

    if (!adminAreaOnly || user.isSuperAdmin) {
      const aggregation = [{ group: { _id: null, ids: { $push: '$_id' } } }];
      const result = await institutionCollection.aggregate(aggregation).toArray();
      return result.at(0)?.ids ?? [];
    }

    // ADMIN
    if (user.isAdmin) {
      return user.adminRole?.contexts ?? [];
    }

    return [];
  }

  public static async _getInstitutionByID(institutionID: string): Promise<Maybe<Institution>> {
    const institutionCollection = mongo().collection<Institution>(Institution._collectionName);
    const institution = await institutionCollection.raw.findOne<Institution>({ _id: institutionID });
    return constructValue(institution);
  }

  static override async beforeCreate(entity: Institution): Promise<void> {
    const raw = (entity as any).doc?.ccMail;
    if (typeof raw === 'string') entity.ccMail = raw.trim();
    return super.beforeCreate(entity);
  }

  static override async beforeUpdate(oldEntity: Institution, update: Institution): Promise<void> {
    const raw = (update as any).doc?.ccMail;
    if (typeof raw === 'string') update.ccMail = raw.trim();
    return super.beforeUpdate(oldEntity, update);
  }
}
